import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader, Mail, Lock, User, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import GrainOverlay from '../components/GrainOverlay';

export default function SubscriptionSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [accountCreated, setAccountCreated] = useState(false);
  
  // Account creation form
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [creatingAccount, setCreatingAccount] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      toast.error('Invalid session');
      navigate('/subscribe');
      return;
    }
    
    verifyPayment();
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      const res = await api.get(`/api/subscriptions/payment-status/${sessionId}`);
      
      if (res.data.payment_status !== 'paid') {
        toast.error('Payment not completed');
        navigate('/subscribe');
        return;
      }
      
      setSession(res.data);
      
      // Check if member account already exists
      try {
        await api.post('/api/members/check-account', {
          email: res.data.customer_email
        });
        setAccountCreated(true);
      } catch (err) {
        // Account doesn't exist - show creation form
        setAccountCreated(false);
      }
      
    } catch (err) {
      console.error('Failed to verify payment:', err);
      toast.error('Failed to verify payment status');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setCreatingAccount(true);
    
    try {
      const res = await api.post('/api/members/create-from-subscription', {
        session_id: sessionId,
        password: password
      });
      
      // Store member token
      localStorage.setItem('memberToken', res.data.token);
      
      toast.success('Account created successfully! 🎉');
      setAccountCreated(true);
      
      // Redirect to members area after 2 seconds
      setTimeout(() => {
        navigate('/members-area');
      }, 2000);
      
    } catch (err) {
      console.error('Failed to create account:', err);
      toast.error(err?.response?.data?.detail || 'Failed to create account');
    } finally {
      setCreatingAccount(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--gio-bg)' }}>
        <GrainOverlay />
        <div className="text-center">
          <Loader size={48} className="animate-spin mx-auto mb-4 text-purple-500" />
          <p className="text-zinc-400">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--gio-bg)' }}>
      <GrainOverlay />
      
      <div className="max-w-md w-full">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={40} className="text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Payment Successful!</h1>
          <p className="text-zinc-400">
            Thank you for subscribing. Your payment has been processed.
          </p>
        </div>

        {/* Account Creation or Login */}
        {!accountCreated ? (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <User size={20} className="text-purple-400" />
              Create Your Account
            </h2>
            
            <p className="text-sm text-zinc-400 mb-6">
              Set a password to access your subscription content.
            </p>

            <form onSubmit={handleCreateAccount} className="space-y-4">
              {/* Email (read-only) */}
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                  <Mail size={14} className="inline mr-1" />
                  Email
                </label>
                <input
                  type="email"
                  value={session?.customer_email || ''}
                  disabled
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-lg px-4 py-2.5 text-zinc-500"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                  <Lock size={14} className="inline mr-1" />
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password (min 8 characters)"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white focus:border-purple-500 outline-none"
                  required
                  minLength={8}
                />
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-400 mb-2">
                  <Lock size={14} className="inline mr-1" />
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2.5 text-white focus:border-purple-500 outline-none"
                  required
                  minLength={8}
                />
              </div>

              <button
                type="submit"
                disabled={creatingAccount}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-lg transition-all flex items-center justify-center gap-2"
              >
                {creatingAccount ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    Create Account & Access Content
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          </div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center">
            <h2 className="text-xl font-bold text-white mb-4">Account Ready!</h2>
            <p className="text-zinc-400 mb-6">
              Your account has been created. Click below to access your content.
            </p>
            <button
              onClick={() => navigate('/members-area')}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold rounded-lg transition-all flex items-center justify-center gap-2"
            >
              Go to Members Area
              <ArrowRight size={18} />
            </button>
          </div>
        )}

        {/* Subscription Details */}
        {session && (
          <div className="mt-6 bg-zinc-950 border border-zinc-800 rounded-lg p-4">
            <div className="text-xs uppercase font-bold text-zinc-500 mb-2">Subscription Details</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-400">Email:</span>
                <span className="text-white">{session.customer_email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Status:</span>
                <span className="text-green-400 font-semibold">Active</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
